<template>
  <div class="indicator-display-config-field">
    <EditableLabel
      :value="displayText"
      class="indicator-display-config-field__editable-label"
      :legend="legend === null ? `${$t('message.CUSTOM_DISPLAY')}${$t('message.TWO_DOTS')}` : legend"
      disabled
      custom-text-classes="generic-border--small-text-btn"
      @click.native="disabled ? null : edit()"
    />
    <IndicatorDisplayConfig
      ref="configModal"
      :displayed="editing"
      :detailed-indicator-hint="detailedIndicatorHint"
      :show-default-wheel-view="showDefaultWheelView"
      @select="onSelect"
      @close="endEdit"
    />
  </div>
</template>

<script>
/**
 * Simple field to pick an Indicator display configuration using IndicatorDisplayConfig component
 *
 * @vue-prop {Number} value - ID of the selected config
 * @vue-prop {String} [legend] - Customize the legend of the field
 * @vue-prop {Boolean} [disabled] - Prevent edition
 * @vue-prop {Boolean} [detailedIndicatorHint] - Whether a detailed hint should be displayed
 * @vue-prop {Boolean} showDefaultWheelView - Whether the option to display wheels should be available or not
 *
 * @vue-event {Number} input - Emits the new value
 */

// State
import { mapState } from 'vuex';
import { FETCH_INDICATOR_DISPLAY_CONFIGS } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import IndicatorDisplayConfig from '@/components/indicator-display-config.vue';
// Utils
import { decodeHTML } from '@/utils/main';

export default {

  name: 'IndicatorDisplayConfigField',

  components: {
    EditableLabel,
    IndicatorDisplayConfig,
  },

  props: {
    value: {
      type: Number,
      required: true,
    },
    legend: {
      type: String,
      default: null,
    },
    disabled: Boolean,
    detailedIndicatorHint: Boolean,
    showDefaultWheelView: Boolean,
  },

  data() {
    return {
      editing: false,
    };
  },

  computed: {
    /** String: Text displayed as the selected value */
    displayText() {
      const config = this.allPresets.find(cfg => cfg.id === this.value);
      if (config) {
        return decodeHTML(config.name);
      }
      return '';
    },
    /** Array: Default and custom configs */
    allPresets() {
      return this.showDefaultWheelView
        ? [...this.$store.getters.indicDisplayConfigs, this.$store.getters.wheelDisplayConfig, ...this.configs]
        : [...this.$store.getters.indicDisplayConfigs, ...this.configs];
    },
    ...mapState({
      configs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      configsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),
  },

  watch: {
    editing() {
      if (this.editing) {
        this.documentBody.appendChild(this.$refs.configModal.$el);
      }
    },
  },

  created() {
    this.refresh();
  },

  beforeDestroy() {
    if (this.$refs.configModal && this.$refs.configModal.$el) {
      this.$refs.configModal.$el.remove();
    }
  },

  methods: {
    /** Fetch available configs */
    refresh() {
      if (!this.configsLoading && this.currentTeam) {
        this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
      }
    },
    /** Start the edition by showing IndicatorDisplayConfig */
    edit() {
      this.editing = true;
      this.$emit('startEdit');
    },
    /** New value selected */
    onSelect(config) {
      if (config && config.id !== undefined) {
        this.$emit('input', config.id);
      }
    },
    /** Close IndicatorDisplayConfig */
    endEdit() {
      this.editing = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.indicator-display-config-field {
  &__editable-label {
    cursor: pointer;
    display: flex;
    align-items: center;
    max-width: 100%;

    .legend {
      flex: 0 0 auto;
      white-space: nowrap;
      margin-right: .5rem;
      margin-bottom: 0;
    }

    > div:last-child {
      min-width: 0px;
      flex: 1;
    }

    .editable-label-text {
      > div {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }
}
</style>
